import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  Clock,
  MessageSquare,
  Loader2,
  Save,
  Bell,
  History,
  ArrowRight,
  MessageCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import StatusChangeDialog from './StatusChangeDialog';
import LeadCallHistory from '../lead/LeadCallHistory';
import ReminderDialog from './ReminderDialog';

const LEAD_STATUSES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead',
];

const VALID_TRANSITIONS = {
  "New Lead": ["Uncontacted", "Qualified Lead", "Nurture", "Dead"],
  "Uncontacted": ["New Lead", "Qualified Lead", "Nurture", "Dead"],
  "Qualified Lead": ["Virtual Meet", "Nurture", "Dead"],
  "Virtual Meet": ["Awaited Visit", "Nurture", "Dead"],
  "Awaited Visit": ["Awaited LOI", "Nurture", "Dead"],
  "Awaited LOI": ["Closed LOI", "Nurture", "Dead"],
  "Closed LOI": ["Outlet Opened", "Dead"],
  "Outlet Opened": [],
  "Nurture": ["New Lead", "Qualified Lead", "Dead"],
  "Dead": ["New Lead", "Nurture"]
};

const getStatusColor = (status) => {
  const colors = {
    'New Lead': 'bg-blue-100 text-blue-800',
    'Uncontacted': 'bg-gray-100 text-gray-800',
    'Qualified Lead': 'bg-indigo-100 text-indigo-800',
    'Virtual Meet': 'bg-purple-100 text-purple-800',
    'Awaited Visit': 'bg-orange-100 text-orange-800',
    'Awaited LOI': 'bg-yellow-100 text-yellow-800',
    'Closed LOI': 'bg-teal-100 text-teal-800',
    'Outlet Opened': 'bg-green-100 text-green-800',
    'Nurture': 'bg-teal-100 text-teal-800',
    'Dead': 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

// WhatsApp message templates
const getWhatsAppMessage = (lead, status) => {
  const meetingData = lead?.virtualMeetData || {};
  const meetingDateTime = meetingData.meetingDateTime 
    ? new Date(meetingData.meetingDateTime).toLocaleString() 
    : '[DATE/TIME]';
  const meetLink = meetingData.googleMeetLink || '[MEET_LINK]';
  
  if (status === 'Virtual Meet') {
    return `Hello ${lead?.name || ''},

Thanks for the information you have given during qualification. I am eager to meet you in Google Meet.

Please join using the link below:
${meetLink}

Date & Time: ${meetingDateTime}

Looking forward to meeting you!

Best regards,
Servall Team`;
  }
  
  if (status === 'Awaited Visit') {
    return `Hello ${lead?.name || ''},

Thank you for the time for Google Meet. You asked important questions and showed interest.

I would like to confirm your visit. Please share your preferred date and time.

Best regards,
Servall Team`;
  }
  
  return `Hello ${lead?.name || ''},

Thank you for your interest in Servall. We would like to follow up with you.

Best regards,
Servall Team`;
};

const openWhatsApp = (mobile, message) => {
  // Clean mobile number
  let cleanNumber = mobile?.replace(/\D/g, '') || '';
  // Add country code if not present
  if (cleanNumber.length === 10) {
    cleanNumber = '91' + cleanNumber;
  }
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://wa.me/${cleanNumber}?text=${encodedMessage}`, '_blank');
};

const LeadDetailsDialog = ({ lead, open, onClose, onUpdate, executives = [] }) => {
  const { api, isManager, user } = useAuth();
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [newReminder, setNewReminder] = useState({ date: '', note: '' });
  const [callDetails, setCallDetails] = useState({ duration: '', outcome: '', notes: '' });
  
  // Status change dialog state
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState(null);
  
  // Reminder dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  
  // Meetings state
  const [meetings, setMeetings] = useState([]);
  const [meetingsLoading, setMeetingsLoading] = useState(false);

  const fetchLeadDetails = useCallback(async () => {
    if (!lead?.id) return;
    try {
      const response = await api.get(`/leads/${lead.id}`);
      setLeadData(response.data);
    } catch (err) {
      toast.error('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [api, lead?.id]);
  
  const fetchMeetings = useCallback(async () => {
    if (!lead?.id) return;
    setMeetingsLoading(true);
    try {
      const response = await api.get(`/meetings/lead/${lead.id}`);
      setMeetings(response.data);
    } catch (err) {
      console.error('Failed to load meetings:', err);
    } finally {
      setMeetingsLoading(false);
    }
  }, [api, lead?.id]);

  useEffect(() => {
    if (open && lead) {
      fetchLeadDetails();
      fetchMeetings();
    }
  }, [open, lead, fetchLeadDetails, fetchMeetings]);

  const handleStatusButtonClick = (newStatus) => {
    setTargetStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusChangeSuccess = () => {
    setStatusDialogOpen(false);
    setTargetStatus(null);
    fetchLeadDetails();
    fetchMeetings(); // Refresh meetings after status change
  };

  const handleAssigneeChange = async (executiveId) => {
    const actualId = executiveId === 'unassigned' ? '' : executiveId;
    const exec = executives.find((e) => e.id === actualId);
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}`, {
        assignedExecutiveId: actualId,
        assignedExecutiveName: exec?.name || '',
      });
      toast.success(actualId ? `Lead assigned to ${exec?.name}` : 'Lead unassigned');
      fetchLeadDetails();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to assign lead';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
      console.error('Single assign error:', err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) return;
    setSaving(true);
    try {
      await api.put(`/leads/${lead.id}`, { remarks: newRemark });
      toast.success('Remark added');
      setNewRemark('');
      fetchLeadDetails();
    } catch (err) {
      toast.error('Failed to add remark');
    } finally {
      setSaving(false);
    }
  };

  const handleAddReminder = async () => {
    if (!newReminder.date) {
      toast.error('Please select a date');
      return;
    }
    setSaving(true);
    try {
      await api.post('/reminders', {
        leadId: lead.id,
        reminderDate: newReminder.date,
        note: newReminder.note,
      });
      toast.success('Reminder set');
      setNewReminder({ date: '', note: '' });
      fetchLeadDetails();
    } catch (err) {
      toast.error('Failed to set reminder');
    } finally {
      setSaving(false);
    }
  };

  const handleLogCall = async () => {
    if (!callDetails.duration) {
      toast.error('Please enter call duration');
      return;
    }
    setSaving(true);
    try {
      await api.post('/activities', {
        leadId: lead.id,
        type: 'call',
        description: `Called lead - ${callDetails.outcome || 'No outcome specified'}`,
        details: {
          duration: callDetails.duration,
          outcome: callDetails.outcome,
          notes: callDetails.notes,
        },
      });
      
      await api.put(`/leads/${lead.id}`, {
        cnrCount: (leadData?.cnrCount || 0) + 1,
      });
      
      toast.success('Call logged');
      setCallDetails({ duration: '', outcome: '', notes: '' });
      fetchLeadDetails();
    } catch (err) {
      toast.error('Failed to log call');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (!leadData) return null;

  const currentStatus = leadData.status || 'New Lead';
  const allowedTransitions = VALID_TRANSITIONS[currentStatus] || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-xl">{leadData.name}</DialogTitle>
                <DialogDescription>
                  Lead ID: {leadData.id.slice(-8)}
                </DialogDescription>
              </div>
              <Badge className={getStatusColor(leadData.status)}>
                {leadData.status}
              </Badge>
            </div>
          </DialogHeader>

          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="status">Status</TabsTrigger>
              <TabsTrigger value="meetings">Meetings</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="actions">Actions</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>{leadData.mobile}</span>
                </div>
                {leadData.email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{leadData.email}</span>
                  </div>
                )}
                {(leadData.city || leadData.state) && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{[leadData.city, leadData.state].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span>Created: {new Date(leadData.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* WhatsApp Quick Action */}
              {leadData.mobile && ['Virtual Meet', 'Awaited Visit'].includes(leadData.status) && (
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">WhatsApp Follow-up</h4>
                      <p className="text-xs text-muted-foreground">Send pre-filled message to lead</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                      onClick={() => openWhatsApp(leadData.mobile, getWhatsAppMessage(leadData, leadData.status))}
                      data-testid="whatsapp-btn"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Send WhatsApp
                    </Button>
                  </div>
                </div>
              )}

              {/* Assignment */}
              {isManager && (
                <div className="pt-4 border-t">
                  <Label>Assigned To</Label>
                  <Select
                    value={leadData.assignedExecutiveId || 'unassigned'}
                    onValueChange={handleAssigneeChange}
                  >
                    <SelectTrigger data-testid="assignee-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {executives.map((exec) => (
                        <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Meta Info */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Badge variant="outline">Source: {leadData.leadSource}</Badge>
                <Badge variant="outline">CNR: {leadData.cnrCount || 0}</Badge>
                {leadData.assignedExecutiveName && (
                  <Badge variant="outline">
                    <User className="mr-1 h-3 w-3" />
                    {leadData.assignedExecutiveName}
                  </Badge>
                )}
              </div>

              {/* Remarks History */}
              {leadData.remarksHistory?.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="mb-2 block">Remarks History</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {leadData.remarksHistory.map((remark, index) => (
                      <div key={index} className="p-2 bg-muted rounded text-sm">
                        <p>{remark.text}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {remark.author} - {new Date(remark.timestamp).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Status Tab - NEW */}
            <TabsContent value="status" className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Status</p>
                    <Badge className={`${getStatusColor(currentStatus)} text-base px-3 py-1`}>
                      {currentStatus}
                    </Badge>
                  </div>
                </div>

                {allowedTransitions.length > 0 ? (
                  <div>
                    <p className="text-sm font-medium mb-3">Move to:</p>
                    <div className="flex flex-wrap gap-2">
                      {allowedTransitions.map((status) => (
                        <Button
                          key={status}
                          variant={status === 'Dead' ? 'destructive' : 'outline'}
                          onClick={() => handleStatusButtonClick(status)}
                          className="flex items-center gap-2"
                          data-testid={`status-btn-${status.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <ArrowRight className="h-4 w-4" />
                          {status}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {currentStatus === 'Outlet Opened' 
                      ? 'This lead has been successfully converted. No further status changes available.'
                      : 'No status transitions available.'}
                  </p>
                )}
              </div>

              {/* Show collected data for each stage */}
              {leadData.qualificationData && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Qualification Data</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div><span className="text-muted-foreground">Interest:</span> {leadData.qualificationData.interestLevel}</div>
                    <div><span className="text-muted-foreground">Urgency:</span> {leadData.qualificationData.urgency}</div>
                    <div><span className="text-muted-foreground">Budget:</span> {leadData.qualificationData.budget}</div>
                    <div><span className="text-muted-foreground">Timeline:</span> {leadData.qualificationData.timelineToStart}</div>
                  </div>
                </div>
              )}

              {leadData.virtualMeetData && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Virtual Meet Data</h4>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Date:</span> {leadData.virtualMeetData.meetingDateTime}</div>
                    <div><span className="text-muted-foreground">Link:</span> <a href={leadData.virtualMeetData.googleMeetLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{leadData.virtualMeetData.googleMeetLink}</a></div>
                  </div>
                </div>
              )}

              {leadData.postMeetingData && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Post-Meeting Data</h4>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Time Taken:</span> {leadData.postMeetingData.timeTaken}</div>
                    <div><span className="text-muted-foreground">Moderated By:</span> {leadData.postMeetingData.whoModerated}</div>
                    <div><span className="text-muted-foreground">Questions:</span> {leadData.postMeetingData.majorQuestions}</div>
                  </div>
                </div>
              )}

              {leadData.loiDetailsData && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">LOI Details</h4>
                  <div className="text-sm space-y-1">
                    <div><span className="text-muted-foreground">Closed By:</span> {leadData.loiDetailsData.whoSatToClose}</div>
                    <div><span className="text-muted-foreground">Models:</span> {leadData.loiDetailsData.modelsDiscussed?.join(', ')}</div>
                    <div><span className="text-muted-foreground">Expected Timeline:</span> {leadData.loiDetailsData.expectedTimeline}</div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Meetings Tab */}
            <TabsContent value="meetings" className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium">Scheduled Meetings</h3>
              </div>
              
              {meetingsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : meetings.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No meetings scheduled yet
                </p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {meetings.map((meeting) => {
                    const isGoogleMeet = meeting.meetingMode === 'google_meet';
                    const meetingDate = new Date(meeting.meetingDateTime);
                    const isPast = meetingDate < new Date();
                    const isLive = meeting.status === 'live';
                    const isCompleted = meeting.status === 'completed';
                    const isCancelled = meeting.status === 'cancelled';
                    
                    return (
                      <div 
                        key={meeting.id} 
                        className={`p-4 rounded-lg border ${isLive ? 'border-green-500 bg-green-50' : isCompleted ? 'bg-muted/30' : 'bg-background'}`}
                        data-testid={`meeting-card-${meeting.id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                isGoogleMeet ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {isGoogleMeet ? 'Google Meet' : 'LMS Virtual Meet'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                isLive ? 'bg-green-100 text-green-700' :
                                isCompleted ? 'bg-gray-100 text-gray-700' :
                                isCancelled ? 'bg-red-100 text-red-700' :
                                isPast ? 'bg-yellow-100 text-yellow-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {isLive ? 'Live' : isCompleted ? 'Completed' : isCancelled ? 'Cancelled' : isPast ? 'Missed' : 'Scheduled'}
                              </span>
                            </div>
                            <p className="text-sm font-medium">
                              {meetingDate.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {meetingDate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {meeting.duration && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Duration: {Math.floor(meeting.duration / 60)} min
                              </p>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {!isCompleted && !isCancelled && (
                              <Button
                                size="sm"
                                variant={isLive ? "default" : "outline"}
                                onClick={() => {
                                  if (isGoogleMeet) {
                                    window.open(meeting.googleMeetLink, '_blank');
                                  } else {
                                    window.open(`/meet/${meeting.roomId}`, '_blank');
                                  }
                                }}
                                className="text-xs"
                                data-testid={`join-meeting-${meeting.id}`}
                              >
                                {isLive ? 'Join Now' : 'Join'}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const link = isGoogleMeet 
                                  ? meeting.googleMeetLink 
                                  : `${window.location.origin}/meet/${meeting.roomId}`;
                                navigator.clipboard.writeText(link);
                                toast.success('Meeting link copied!');
                              }}
                              className="text-xs"
                              data-testid={`copy-link-${meeting.id}`}
                            >
                              Copy Link
                            </Button>
                          </div>
                        </div>
                        
                        {/* Show transcript/summary for completed LMS meetings */}
                        {isCompleted && !isGoogleMeet && (meeting.summary || meeting.transcript) && (
                          <div className="mt-3 pt-3 border-t space-y-3">
                            {meeting.summary && (
                              <div>
                                <h4 className="text-xs font-semibold text-primary mb-2 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                  </svg>
                                  AI Summary
                                </h4>
                                <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-md whitespace-pre-wrap">
                                  {meeting.summary}
                                </div>
                              </div>
                            )}
                            
                            {meeting.transcript && (
                              <details className="group">
                                <summary className="text-xs font-semibold text-primary cursor-pointer flex items-center gap-1 hover:underline">
                                  <svg className="w-3 h-3 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  View Full Transcript
                                </summary>
                                <div className="mt-2 text-xs text-muted-foreground bg-gray-50 dark:bg-gray-800 p-3 rounded-md max-h-40 overflow-y-auto whitespace-pre-wrap">
                                  {meeting.transcript}
                                </div>
                              </details>
                            )}
                            
                            {meeting.summaryGeneratedAt && (
                              <p className="text-[10px] text-muted-foreground">
                                Summary generated: {new Date(meeting.summaryGeneratedAt).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-4">
              {leadData.activities?.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No activity recorded yet
                </p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {leadData.activities?.map((activity) => (
                    <div key={activity.id} className="flex gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="p-2 rounded-full bg-primary/10 h-fit">
                        <Clock className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm">{activity.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {activity.author} - {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Actions Tab */}
            <TabsContent value="actions" className="space-y-6">
              {/* Add Remark */}
              <div>
                <Label>Add Remark</Label>
                <div className="flex gap-2 mt-2">
                  <Textarea
                    value={newRemark}
                    onChange={(e) => setNewRemark(e.target.value)}
                    placeholder="Enter a note about this lead..."
                    className="flex-1"
                    data-testid="remark-input"
                  />
                </div>
                <Button
                  onClick={handleAddRemark}
                  disabled={saving || !newRemark.trim()}
                  className="mt-2"
                  data-testid="add-remark-btn"
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  Add Remark
                </Button>
              </div>

              {/* Log Call */}
              <div className="pt-4 border-t">
                <Label>Log Call</Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <Label className="text-xs">Duration (minutes)</Label>
                    <Input
                      type="number"
                      value={callDetails.duration}
                      onChange={(e) => setCallDetails({ ...callDetails, duration: e.target.value })}
                      placeholder="5"
                      data-testid="call-duration-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Outcome</Label>
                    <Select
                      value={callDetails.outcome}
                      onValueChange={(v) => setCallDetails({ ...callDetails, outcome: v })}
                    >
                      <SelectTrigger data-testid="call-outcome-select">
                        <SelectValue placeholder="Select outcome" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Connected">Connected</SelectItem>
                        <SelectItem value="No Answer">No Answer</SelectItem>
                        <SelectItem value="Busy">Busy</SelectItem>
                        <SelectItem value="Wrong Number">Wrong Number</SelectItem>
                        <SelectItem value="Callback Requested">Callback Requested</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Textarea
                  value={callDetails.notes}
                  onChange={(e) => setCallDetails({ ...callDetails, notes: e.target.value })}
                  placeholder="Call notes..."
                  className="mt-2"
                  data-testid="call-notes-input"
                />
                <Button
                  onClick={handleLogCall}
                  disabled={saving || !callDetails.duration}
                  className="mt-2"
                  data-testid="log-call-btn"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Log Call
                </Button>
              </div>

              {/* Phase A — Call Tracking analytics + timeline */}
              <div className="pt-4 border-t">
                <LeadCallHistory
                  leadId={leadData._id || leadData.id}
                  leadName={leadData.name}
                  leadMobile={leadData.mobile}
                />
              </div>

              {/* Set Reminder */}
              <div className="pt-4 border-t">
                <Label>Set Reminder</Label>
                <p className="text-sm text-muted-foreground mb-2">Schedule follow-ups, calls, or meetings</p>
                <Button
                  onClick={() => setReminderDialogOpen(true)}
                  variant="outline"
                  className="w-full"
                  data-testid="set-reminder-btn"
                >
                  <Bell className="mr-2 h-4 w-4" />
                  Set Reminder
                </Button>
              </div>
            </TabsContent>

            {/* Status History Tab - NEW */}
            <TabsContent value="history" className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <History className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-medium">Status Change History</h3>
              </div>
              
              {(!leadData.statusHistory || leadData.statusHistory.length === 0) ? (
                <p className="text-center text-muted-foreground py-8">
                  No status history recorded
                </p>
              ) : (
                <div className="space-y-3">
                  {[...leadData.statusHistory].reverse().map((entry, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        {entry.from && (
                          <>
                            <Badge variant="outline" className="text-xs">{entry.from}</Badge>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </>
                        )}
                        <Badge className={getStatusColor(entry.to)}>{entry.to}</Badge>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>By: {entry.userName}</p>
                        <p>At: {new Date(entry.timestamp).toLocaleString()}</p>
                      </div>
                      {entry.data && Object.keys(entry.data).length > 0 && (
                        <div className="mt-2 pt-2 border-t text-sm">
                          <p className="font-medium text-xs text-muted-foreground mb-1">Data Captured:</p>
                          <div className="grid grid-cols-2 gap-1">
                            {Object.entries(entry.data).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}
                                <span>{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button onClick={onUpdate} data-testid="refresh-lead-btn">
              <Save className="mr-2 h-4 w-4" />
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      {targetStatus && (
        <StatusChangeDialog
          open={statusDialogOpen}
          onClose={() => {
            setStatusDialogOpen(false);
            setTargetStatus(null);
          }}
          lead={leadData}
          targetStatus={targetStatus}
          onSuccess={handleStatusChangeSuccess}
        />
      )}
      
      {/* Reminder Dialog */}
      <ReminderDialog
        open={reminderDialogOpen}
        onClose={() => setReminderDialogOpen(false)}
        leadId={lead?.id}
        leadName={leadData?.name}
        onSuccess={() => {
          toast.success('Reminder created for this lead');
        }}
      />
    </>
  );
};

export default LeadDetailsDialog;
